import { useState } from 'react';
import { t } from '../lib/i18n';
import type { FieldDescriptor, FilterCondition, FilterGroup } from './api';

/**
 * A small filter builder over the field catalogue the API publishes.
 *
 * The operators offered depend on the field's type, mirroring what the engine accepts, so the UI
 * cannot compose a filter the server will reject.
 */

const OPERATORS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  text: [
    { value: 'contains', label: 'contém' },
    { value: 'eq', label: 'é igual a' },
    { value: 'neq', label: 'é diferente de' },
    { value: 'starts_with', label: 'começa com' },
    { value: 'empty', label: 'está vazio' },
    { value: 'not_empty', label: 'não está vazio' },
  ],
  number: [
    { value: 'eq', label: 'é igual a' },
    { value: 'gt', label: 'maior que' },
    { value: 'lt', label: 'menor que' },
    { value: 'empty', label: 'está vazio' },
  ],
  money: [
    { value: 'gte', label: 'a partir de' },
    { value: 'lte', label: 'até' },
    { value: 'eq', label: 'é igual a' },
  ],
  enum: [
    { value: 'eq', label: 'é' },
    { value: 'neq', label: 'não é' },
  ],
  datetime: [
    { value: 'after', label: 'depois de' },
    { value: 'before', label: 'antes de' },
  ],
  date: [
    { value: 'after', label: 'depois de' },
    { value: 'before', label: 'antes de' },
  ],
  boolean: [
    { value: 'is_true', label: 'é verdadeiro' },
    { value: 'is_false', label: 'é falso' },
  ],
  uuid: [{ value: 'eq', label: 'é' }],
};

const VALUELESS_OPERATORS = new Set(['empty', 'not_empty', 'is_true', 'is_false']);

interface Props {
  fields: FieldDescriptor[];
  value: FilterGroup;
  onChange: (filter: FilterGroup) => void;
}

export function FilterBuilder({ fields, value, onChange }: Props): React.JSX.Element {
  const [draftField, setDraftField] = useState(fields[0]?.key ?? '');

  const conditions = value.conditions.filter(
    (condition): condition is FilterCondition => !('combinator' in condition),
  );

  function fieldFor(key: string): FieldDescriptor | undefined {
    return fields.find((field) => field.key === key);
  }

  function operatorsFor(key: string): { value: string; label: string }[] {
    const type = fieldFor(key)?.type ?? 'text';
    return OPERATORS_BY_TYPE[type] ?? OPERATORS_BY_TYPE.text!;
  }

  function addCondition(): void {
    if (!draftField) return;

    const operator = operatorsFor(draftField)[0]?.value ?? 'eq';
    onChange({ ...value, conditions: [...conditions, { field: draftField, operator, value: '' }] });
  }

  function updateCondition(index: number, patch: Partial<FilterCondition>): void {
    const next = conditions.map((condition, position) =>
      position === index ? { ...condition, ...patch } : condition,
    );

    onChange({ ...value, conditions: next });
  }

  function removeCondition(index: number): void {
    onChange({ ...value, conditions: conditions.filter((_, position) => position !== index) });
  }

  return (
    <div className="filters">
      <div className="filters__header">
        <label className="field field--inline">
          <span>{t('filters.combinator')}</span>
          <select
            value={value.combinator}
            onChange={(event) =>
              onChange({ ...value, combinator: event.target.value as 'AND' | 'OR' })
            }
          >
            <option value="AND">{t('filters.and')}</option>
            <option value="OR">{t('filters.or')}</option>
          </select>
        </label>
      </div>

      {conditions.map((condition, index) => {
        const descriptor = fieldFor(condition.field);
        const needsValue = !VALUELESS_OPERATORS.has(condition.operator);

        return (
          <div className="filters__row" key={`${condition.field}-${index}`}>
            <select
              aria-label={t('filters.field')}
              value={condition.field}
              onChange={(event) =>
                updateCondition(index, {
                  field: event.target.value,
                  operator: operatorsFor(event.target.value)[0]?.value ?? 'eq',
                  value: '',
                })
              }
            >
              {fields.map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
            </select>

            <select
              aria-label={t('filters.operator')}
              value={condition.operator}
              onChange={(event) => updateCondition(index, { operator: event.target.value })}
            >
              {operatorsFor(condition.field).map((operator) => (
                <option key={operator.value} value={operator.value}>
                  {operator.label}
                </option>
              ))}
            </select>

            {needsValue &&
              (descriptor?.options ? (
                <select
                  aria-label={t('filters.value')}
                  value={String(condition.value ?? '')}
                  onChange={(event) => updateCondition(index, { value: event.target.value })}
                >
                  <option value="">—</option>
                  {descriptor.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  aria-label={t('filters.value')}
                  value={String(condition.value ?? '')}
                  onChange={(event) => updateCondition(index, { value: event.target.value })}
                />
              ))}

            <button type="button" className="ghost" onClick={() => removeCondition(index)}>
              {t('filters.remove')}
            </button>
          </div>
        );
      })}

      <div className="filters__row">
        <select
          aria-label={t('filters.addField')}
          value={draftField}
          onChange={(event) => setDraftField(event.target.value)}
        >
          {fields.map((field) => (
            <option key={field.key} value={field.key}>
              {field.label}
            </option>
          ))}
        </select>

        <button type="button" onClick={addCondition}>
          {t('filters.add')}
        </button>
      </div>
    </div>
  );
}
